/**
 * Batch Progress Policy (TV1-1688)
 *
 * Subscribes to the new `/batch-processes/progress/:batchProcessId?`
 * readmodel in signal-mode and refetches the projected success count from
 * `/batch-processes/progress-count/:batchProcessId` whenever a change
 * signal arrives.
 *
 * Memory note: pv3 signal-mode subscriptions don't auto-refetch — consumers
 * implement refetch on `{ type: 'signal' }`. Refetches are debounced to at most
 * once per 250 ms to avoid socket flooding on large batches.
 */

import { Policy } from '@commandDrivenStateEngine/core/policies';
import { ClientLibraryType, ReadmodelChange } from '@bett3r-dev/teselly-client-library';
import { BatchProgressType } from '@bett3r-dev/teselly-domain';
import { BatchProcessesDetailState } from '../state';
import { BatchProcessesDetailCommand } from '../commands';

const SIGNAL_DEBOUNCE_MS = 250;

export const createBatchProgressPolicy = (
  backend: ClientLibraryType,
  processId: string
): Policy<BatchProcessesDetailState, BatchProcessesDetailCommand> => ({
  name: 'batch-progress-count',

  init: ({ dispatch }) => {
    let debounceTimer: ReturnType<typeof setTimeout> | null = null;
    let closed = false;

    const fetchCount = async () => {
      if ( closed ) return;
      try {
        // The count query exposes `{ count: number }` for the given batch id.
        const result = await backend.queries.BatchProcessesProgressCount({
          batchProcessId: processId
        });
        if ( closed ) return;
        dispatch(
          {
            type: 'batchProcesses/detail/data/setProjectedSuccessCount',
            payload: { count: Number( result?.count ) || 0 }
          },
          { source: 'api' }
        );
      } catch ( error ) {
        if ( closed ) return;
        console.error( 'Error fetching batch progress count:', error );
        dispatch(
          {
            type: 'batchProcesses/detail/loading/setProjectedSuccessCountLoading',
            payload: { isLoading: false }
          },
          { source: 'api' }
        );
      }
    };

    const scheduleFetch = () => {
      if ( debounceTimer ) clearTimeout( debounceTimer );
      debounceTimer = setTimeout(() => {
        debounceTimer = null;
        fetchCount();
      }, SIGNAL_DEBOUNCE_MS );
    };

    // Initial load — kick the count immediately so the bar is populated before
    // the first signal arrives.
    dispatch(
      {
        type: 'batchProcesses/detail/loading/setProjectedSuccessCountLoading',
        payload: { isLoading: true }
      },
      { source: 'api' }
    );
    fetchCount();

    // Signal-mode subscription — server sends `{ type: 'signal' }` pings only.
    // No filter needed: fetchCount() is already scoped to processId.
    const subscription = backend.subscriptions.BatchProcessesProgress(
      {
        _snapshot: false,
        _changes: 'signal'
      },
      ( change: ReadmodelChange<BatchProgressType> ) => {
        if ( change?.type === 'signal' ) {
          scheduleFetch();
        }
      }
    );

    return () => {
      closed = true;
      if ( debounceTimer ) {
        clearTimeout( debounceTimer );
        debounceTimer = null;
      }
      if ( subscription && typeof subscription.close === 'function' ) {
        subscription.close();
      }
    };
  }
});
